import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { formatEatDateTime } from '@wodi/domain';
import type {
  IssuedAccountLink,
  SessionView,
  TwoFactorResetView,
  UserSecurityView,
} from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  Section,
  Tag,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  decideTwoFactorReset,
  issuePasswordResetLink,
  openTwoFactorResetsQuery,
  requestTwoFactorReset,
  userSecurityQuery,
} from '../api/account-security.ts';
import { ApiError } from '../api/client.ts';
import { can } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { screen } from '../layout/screen.tsx';
import { toasts } from '../toasts.ts';
import styles from './user-security.module.css';

// Settings > Users > a person: their sign-in security (ADR 0037). A password reset link is
// shown once, to hand over in person. A two-factor reset is asked for with a reason by one
// administrator and approved by another.

const when = (iso: string) => formatEatDateTime(new Date(iso));

function messageOf(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/** Shows a just-issued link once. Closing the dialog is the last time anyone sees it. */
function IssuedLinkDialog({
  link,
  name,
  onClose,
}: {
  link: IssuedAccountLink;
  name: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <Dialog
      title="Password reset link"
      isOpen
      isDismissable={false}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p>
        Give this link to {name} in person. It works once, until {when(link.expiresAt)}, and
        won&apos;t be shown again.
      </p>
      <TextField label="Link" value={link.url} isReadOnly />
      {copied ? <Notice tone="info">Link copied.</Notice> : null}
      <DialogActions>
        <Button
          onPress={() => {
            void navigator.clipboard.writeText(link.url).then(() => {
              setCopied(true);
            });
          }}
        >
          Copy link
        </Button>
        <Button variant="primary" onPress={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ResetLinkAction({
  userId,
  name,
  online,
}: {
  userId: string;
  name: string;
  online: boolean;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const issue = useMutation({
    mutationFn: () => issuePasswordResetLink(userId),
    onSuccess: async () => {
      setConfirming(false);
      await queryClient.invalidateQueries({ queryKey: userSecurityQuery(userId).queryKey });
    },
  });
  return (
    <>
      <Button
        isDisabled={!online}
        onPress={() => {
          issue.reset();
          setConfirming(true);
        }}
      >
        Issue password reset link
      </Button>
      {confirming ? (
        <Dialog
          title="Issue a password reset link?"
          isOpen
          onOpenChange={(open) => {
            if (!open) setConfirming(false);
          }}
        >
          <p>
            Whoever has the link can set a new password for {name} for the next 24 hours. Any
            earlier link stops working. Hand it over in person.
          </p>
          {issue.error === null ? null : (
            <Notice tone="error">
              {messageOf(issue.error, "The link wasn't issued. Try again.")}
            </Notice>
          )}
          <DialogActions>
            <Button
              variant="quiet"
              onPress={() => {
                setConfirming(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              isPending={issue.isPending}
              pendingLabel="Issuing…"
              onPress={() => {
                issue.mutate();
              }}
            >
              Issue link
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
      {issue.data === undefined ? null : (
        <IssuedLinkDialog
          link={issue.data}
          name={name}
          onClose={() => {
            issue.reset();
          }}
        />
      )}
    </>
  );
}

function RequestResetAction({
  userId,
  name,
  online,
}: {
  userId: string;
  name: string;
  online: boolean;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const request = useMutation({
    mutationFn: () => requestTwoFactorReset(userId, reason.trim()),
    onSuccess: async () => {
      setOpen(false);
      setReason('');
      toasts.show({ title: 'Two-factor reset asked for' });
      await queryClient.invalidateQueries({ queryKey: ['two-factor-resets'] });
      await queryClient.invalidateQueries({ queryKey: userSecurityQuery(userId).queryKey });
    },
  });
  return (
    <>
      <Button
        isDisabled={!online}
        onPress={() => {
          request.reset();
          setOpen(true);
        }}
      >
        Ask for a two-factor reset
      </Button>
      {open ? (
        <Dialog
          title="Ask for a two-factor reset"
          isOpen
          onOpenChange={(next) => {
            if (!next) setOpen(false);
          }}
        >
          <p>
            Another administrator must approve it. Then {name} is signed out everywhere and sets up
            two-factor sign-in again at their next sign-in.
          </p>
          <TextArea
            label="Reason"
            description="For example: lost phone and recovery codes. Saved in the audit log."
            value={reason}
            onChange={setReason}
          />
          {request.error === null ? null : (
            <Notice tone="error">
              {messageOf(request.error, "The reset wasn't asked for. Try again.")}
            </Notice>
          )}
          <DialogActions>
            <Button
              variant="quiet"
              onPress={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={reason.trim() === ''}
              isPending={request.isPending}
              pendingLabel="Sending…"
              onPress={() => {
                request.mutate();
              }}
            >
              Ask for reset
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </>
  );
}

/** Approve or reject an open reset; shown only to a second administrator who may decide. */
export function DecideReset({ reset, online }: { reset: TwoFactorResetView; online: boolean }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'reject') => decideTwoFactorReset(reset.id, decision),
    onSuccess: async (result) => {
      setConfirming(false);
      toasts.show({
        title:
          result.status === 'approved' ? 'Two-factor reset approved' : 'Two-factor reset rejected',
      });
      await queryClient.invalidateQueries({ queryKey: ['two-factor-resets'] });
      await queryClient.invalidateQueries({ queryKey: ['users', reset.userId] });
    },
  });
  return (
    <div className={styles.actions}>
      {decide.error === null || confirming ? null : (
        <Notice tone="error">
          {messageOf(decide.error, "The reset wasn't decided. Try again.")}
        </Notice>
      )}
      <Button
        variant="primary"
        isDisabled={!online}
        onPress={() => {
          decide.reset();
          setConfirming(true);
        }}
      >
        Approve reset
      </Button>
      <Button
        variant="quiet"
        isDisabled={!online}
        isPending={decide.isPending && !confirming}
        pendingLabel="Rejecting…"
        onPress={() => {
          decide.mutate('reject');
        }}
      >
        Reject
      </Button>
      {confirming ? (
        <Dialog
          title="Approve the two-factor reset?"
          isOpen
          onOpenChange={(open) => {
            if (!open) setConfirming(false);
          }}
        >
          <p>
            {reset.userDisplayName} will be signed out everywhere, their authenticator app and
            recovery codes will stop working, and they set up two-factor sign-in again at their next
            sign-in.
          </p>
          {decide.error === null ? null : (
            <Notice tone="error">
              {messageOf(decide.error, "The reset wasn't approved. Try again.")}
            </Notice>
          )}
          <DialogActions>
            <Button
              variant="quiet"
              onPress={() => {
                setConfirming(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              isPending={decide.isPending}
              pendingLabel="Approving…"
              onPress={() => {
                decide.mutate('approve');
              }}
            >
              Approve reset
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </div>
  );
}

function ResetNotice({
  reset,
  session,
  online,
}: {
  reset: TwoFactorResetView;
  session: SessionView;
  online: boolean;
}) {
  const mayDecide =
    can(session, 'admin.users.two_factor_reset.approve') &&
    reset.requestedBy.id !== session.user.id &&
    reset.userId !== session.user.id;
  return (
    <Notice tone="info">
      <p>
        {reset.requestedBy.displayName} asked for a two-factor reset on {when(reset.requestedAt)}: “
        {reset.reason}”. {mayDecide ? '' : 'It waits for a second administrator to approve it.'}
      </p>
      {mayDecide ? <DecideReset reset={reset} online={online} /> : null}
    </Notice>
  );
}

function Facts({ security }: { security: UserSecurityView }) {
  return (
    <dl className={styles.facts}>
      <dt>Password</dt>
      <dd>
        {security.passwordSet && security.passwordChangedAt !== null
          ? `Set, last changed ${when(security.passwordChangedAt)}`
          : 'Not set yet'}
      </dd>
      <dt>Two-factor sign-in</dt>
      <dd>
        {security.twoFactor.enrolled ? (
          <Tag state="done">Set up</Tag>
        ) : security.twoFactor.required ? (
          <Tag state="pending">Needed at next sign-in</Tag>
        ) : (
          <Tag state="neutral">Not needed for their roles</Tag>
        )}
      </dd>
      {security.lockedUntil === null ? null : (
        <>
          <dt>Locked</dt>
          <dd>Until {when(security.lockedUntil)}, after too many wrong attempts</dd>
        </>
      )}
      {security.liveLink === null ? null : (
        <>
          <dt>Link</dt>
          <dd>
            A {security.liveLink.purpose === 'setup' ? 'setup' : 'password reset'} link works until{' '}
            {when(security.liveLink.expiresAt)}
          </dd>
        </>
      )}
    </dl>
  );
}

export function UserSecuritySection({
  userId,
  name,
  session,
}: {
  userId: string;
  name: string;
  session: SessionView;
}) {
  const online = useOnline();
  const security = useQuery(userSecurityQuery(userId));
  const self = userId === session.user.id;
  return (
    <Section title="Sign-in security" description="Passwords and two-factor sign-in.">
      {security.isPending ? <LoadingBlock label="Loading sign-in security" rows={3} /> : null}
      {security.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense isDisabled={!online} onPress={() => void security.refetch()}>
              Try again
            </Button>
          }
        >
          {online
            ? messageOf(security.error, "Sign-in security couldn't be loaded. Try again.")
            : "You're offline. Sign-in security can be loaded once you reconnect."}
        </Notice>
      ) : null}
      {security.data === undefined ? null : (
        <div className={screen.page}>
          <Facts security={security.data} />
          {security.data.openReset === null ? null : (
            <ResetNotice reset={security.data.openReset} session={session} online={online} />
          )}
          {self ? (
            <p>
              To change your own password, use <Link to="/account">Account</Link>. Another
              administrator resets your two-factor sign-in.
            </p>
          ) : (
            <div className={styles.actions}>
              {can(session, 'admin.users.password_reset') ? (
                <ResetLinkAction userId={userId} name={name} online={online} />
              ) : null}
              {can(session, 'admin.users.two_factor_reset.request') &&
              security.data.twoFactor.enrolled &&
              security.data.openReset === null ? (
                <RequestResetAction userId={userId} name={name} online={online} />
              ) : null}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

/** Settings > Users: two-factor resets waiting for a second administrator. */
export function OpenTwoFactorResets({ session }: { session: SessionView }) {
  const online = useOnline();
  const resets = useQuery(openTwoFactorResetsQuery);
  if (resets.isError) {
    return (
      <Notice tone="error">
        {messageOf(resets.error, "Two-factor resets waiting for approval couldn't be loaded.")}
      </Notice>
    );
  }
  const items = resets.data?.items ?? [];
  if (items.length === 0) return null;
  // On the Users card already, so a notice rather than a card of its own (cards never nest).
  return (
    <Notice tone="info">
      <p>
        <strong>Two-factor resets waiting for approval</strong>
      </p>
      <ul className={styles.list}>
        {items.map((reset) => (
          <li key={reset.id}>
            <span>
              <Link to="/settings/users/$userId" params={{ userId: reset.userId }}>
                {reset.userDisplayName}
              </Link>
              , asked for by {reset.requestedBy.displayName} on {when(reset.requestedAt)}: “
              {reset.reason}”
            </span>
            {can(session, 'admin.users.two_factor_reset.approve') &&
            reset.requestedBy.id !== session.user.id &&
            reset.userId !== session.user.id ? (
              <DecideReset reset={reset} online={online} />
            ) : null}
          </li>
        ))}
      </ul>
    </Notice>
  );
}
